import { useEffect, useState } from 'react';
import { DollarSign, Shield, TrendingUp, Clock } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Table } from '@/components/ui/Table';
import { Spinner } from '@/components/ui/Spinner';
import api from '@/lib/api';
import type { ApiResponse, EscrowWallet } from '@hirenfire/shared';
import { CHAIN_CONFIGS } from '@hirenfire/shared';

interface EscrowOverview {
  totalActive: number;
  totalHeld: number;
  recentReleases: number;
  pendingDeposits: number;
}

export function AdminEscrowPage() {
  const [overview, setOverview] = useState<EscrowOverview>({
    totalActive: 0,
    totalHeld: 0,
    recentReleases: 0,
    pendingDeposits: 0,
  });
  const [escrows, setEscrows] = useState<EscrowWallet[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [overviewRes, escrowsRes] = await Promise.all([
          api.get<ApiResponse<EscrowOverview>>('/admin/escrow/overview'),
          api.get<ApiResponse<EscrowWallet[]>>('/admin/escrow/wallets'),
        ]);
        if (overviewRes.data.data) setOverview(overviewRes.data.data);
        if (escrowsRes.data.data) setEscrows(escrowsRes.data.data);
      } catch {
        // fallback
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Escrow Management</h1>
        <p className="text-gray-400 mt-1">Monitor platform escrow wallets</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Active Escrows</p>
                <p className="text-2xl font-bold text-white">{overview.totalActive}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-success-500/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-success-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Total Held</p>
                <p className="text-2xl font-bold text-white">
                  ${overview.totalHeld.toLocaleString()}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-accent-500/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-accent-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Recent Releases</p>
                <p className="text-2xl font-bold text-white">{overview.recentReleases}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-warning-500/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-warning-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Pending Deposits</p>
                <p className="text-2xl font-bold text-white">{overview.pendingDeposits}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>Escrow Wallets</CardHeader>
        <Table
          keyExtractor={(e) => e.id}
          emptyMessage="No escrow wallets"
          columns={[
            {
              key: 'contractId',
              header: 'Contract',
              render: (e) => (
                <span className="font-mono text-sm text-gray-300">
                  {e.contractId.slice(0, 8)}...
                </span>
              ),
            },
            {
              key: 'address',
              header: 'Address',
              render: (e) => (
                <code className="font-mono text-xs text-gray-400">
                  {e.address.slice(0, 12)}...{e.address.slice(-6)}
                </code>
              ),
            },
            {
              key: 'chain',
              header: 'Chain',
              render: (e) => (
                <Badge variant="primary">{CHAIN_CONFIGS[e.chain].name}</Badge>
              ),
            },
            {
              key: 'balance',
              header: 'Balance',
              render: (e) => (
                <span className="font-semibold text-white">${e.balance.toLocaleString()}</span>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              render: (e) => (
                <Badge
                  variant={
                    e.status === 'FUNDED' ? 'success' :
                    e.status === 'RELEASED' ? 'primary' :
                    e.status === 'DISPUTED' ? 'error' : 'warning'
                  }
                >
                  {e.status}
                </Badge>
              ),
            },
          ]}
          data={escrows}
        />
      </Card>
    </div>
  );
}
